
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../../components/common/ModalCloseX";
import SortableTh from "../../components/common/SortableTh";
import InfoTooltip from "../../components/common/InfoTooltip";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import useTableSort from "../../hooks/useTableSort";
import useHelpGuideNavigation from "../../hooks/useHelpGuideNavigation";
import { useAuth } from "../../context/AuthContext";
import {
  getSurveySatisfactionSummary,
  getSurveySatisfactionByStaff,
  getSurveySatisfactionTrends,
} from "../../api/clientSurveys";
import { getSalonTimezone } from "../../utils/salonTimezone";
import { clientSatisfactionTooltips } from "../../constants/clientSatisfactionHelp";
import { REPORT_HELP } from "../../constants/reportHelpContent";
import "../ReportsSectionCompact.css";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../../ui/modalStyles";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${(num * 100).toFixed(1)}%`;
};

const formatRating = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toFixed(2).replace(/\.?0+$/, "");
};

const formatHours = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(1)} hrs`;
};

const csvEscape = (value) => {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
    return `"${str.replace(/"/g, "\"\"")}"`;
  }
  return str;
};

const downloadCsv = (filename, headers, rows) => {
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const SummaryCard = ({ label, value, hint }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
    {hint ? <div className="mt-1 text-sm text-slate-500">{hint}</div> : null}
  </div>
);

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
    <div className="font-semibold text-slate-700">{label}</div>
    <div className="text-slate-600">{value}</div>
  </div>
);
export default function ClientSatisfactionDashboard() {
  const navigate = useNavigate();
  const { currentSalon, user, token } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.CLIENT_SATISFACTION;

  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }, [currentSalon, user?.salon_id]);

  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);
  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -29), salonTimezone);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [bucket, setBucket] = useState("week");
  const [summary, setSummary] = useState(null);
  const [staffRows, setStaffRows] = useState([]);
  const [trendRows, setTrendRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/reports", { replace: true });
  };

  const loadReport = useCallback(async () => {
    if (!resolvedSalonId) {
      setError("Select a salon to view this report.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    if (startDate > endDate) {
      setError("End date can't be before start date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [summaryResult, staffResult, trendResult] = await Promise.allSettled([
        getSurveySatisfactionSummary({
          salon_id: resolvedSalonId,
          start_date: startDate,
          end_date: endDate,
        }, token),
        getSurveySatisfactionByStaff({
          salon_id: resolvedSalonId,
          start_date: startDate,
          end_date: endDate,
        }, token),
        getSurveySatisfactionTrends({
          salon_id: resolvedSalonId,
          start_date: startDate,
          end_date: endDate,
          bucket,
        }, token),
      ]);

      if (summaryResult.status === "fulfilled") {
        setSummary(summaryResult.value || null);
      } else {
        setSummary(null);
        setError(
          summaryResult.reason?.response?.data?.detail ||
            "Unable to load summary."
        );
      }

      if (staffResult.status === "fulfilled") {
        setStaffRows(Array.isArray(staffResult.value) ? staffResult.value : []);
      } else {
        setStaffRows([]);
      }

      if (trendResult.status === "fulfilled") {
        setTrendRows(Array.isArray(trendResult.value) ? trendResult.value : []);
      } else {
        setTrendRows([]);
      }
    } finally {
      setLoading(false);
    }
  }, [resolvedSalonId, startDate, endDate, bucket, token]);
  const renderLabelWithTooltip = (labelText, tooltipKey, className = "") => (
    <span className={`inline-flex items-center gap-2 ${className}`.trim()}>
      <span>{labelText}</span>
      <InfoTooltip
        text={clientSatisfactionTooltips[tooltipKey]}
        label={labelText}
      />
    </span>
  );

  const totalSurveys = summary?.total_surveys ?? 0;

  const distributionRows = useMemo(() => {
    const map = new Map();
    (summary?.distribution || []).forEach((row) => {
      if (row?.rating != null) {
        map.set(Number(row.rating), Number(row.count || 0));
      }
    });
    return [5, 4, 3, 2, 1].map((rating) => ({
      rating,
      count: map.get(rating) || 0,
      percent: totalSurveys ? (map.get(rating) || 0) / totalSurveys : 0,
    }));
  }, [summary?.distribution, totalSurveys]);

  const criteriaRows = useMemo(() => {
    return [
      { label: "Salon", value: formatRating(summary?.avg_salon) },
      { label: "Reception", value: formatRating(summary?.avg_receptionist) },
      { label: "Service", value: formatRating(summary?.avg_service) },
      { label: "Staff", value: formatRating(summary?.avg_staff) },
      { label: "Online booking", value: formatRating(summary?.avg_online_booking) },
    ];
  }, [summary]);

  const chartPoints = useMemo(() => {
    const rows = Array.isArray(trendRows) ? trendRows : [];
    if (rows.length === 0) return { points: "", max: 5, rows: [] };
    const maxValue = Math.max(
      5,
      ...rows.map((row) => Number(row?.avg_overall || 0))
    );
    if (rows.length === 1) {
      const y = 20 - (Number(rows[0]?.avg_overall || 0) / maxValue) * 20;
      return { points: `50,${y}`, max: maxValue, rows };
    }
    const points = rows
      .map((row, idx) => {
        const x = (idx / (rows.length - 1)) * 100;
        const y = 40 - (Number(row?.avg_overall || 0) / maxValue) * 40;
        return `${x},${y}`;
      })
      .join(" ");
    return { points, max: maxValue, rows };
  }, [trendRows]);

  const staffColumns = useMemo(
    () => ({
      staff_name: { type: "text" },
      survey_count: { type: "number" },
      avg_overall: { type: "number" },
      avg_service: { type: "number" },
      avg_staff: { type: "number" },
      below_four_percent: { type: "number" },
    }),
    []
  );

  const staffSort = useTableSort({
    rows: staffRows,
    columns: staffColumns,
    defaultSortBy: "survey_count",
  });

  const handleExport = (type) => {
    if (type === "staff") {
      const rows = staffSort.sortedRows.map((row) => [
        row.staff_name || "Unassigned",
        row.survey_count ?? 0,
        formatRating(row.avg_overall),
        formatRating(row.avg_service),
        formatRating(row.avg_staff),
        formatPercent(row.below_four_percent),
      ]);
      downloadCsv(
        "client_satisfaction_staff_breakdown.csv",
        [
          "Staff",
          "Feedback",
          "Avg Overall",
          "Avg Service",
          "Avg Staff",
          "% Below 4",
        ],
        rows
      );
    }
  };
  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Client Satisfaction
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="client-satisfaction-overview"
              className={getSectionClassName("client-satisfaction-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="client-satisfaction-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                "client-satisfaction-filters"
              )}`}
            >
              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[180px]">
                  <div className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
                    {renderLabelWithTooltip("Start date", "start_date")}
                  </div>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  />
                </div>
                <div className="min-w-[180px]">
                  <div className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
                    {renderLabelWithTooltip("End date", "end_date")}
                  </div>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  />
                </div>
                <div className="min-w-[180px]">
                  <div className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
                    {renderLabelWithTooltip("Trend bucket", "trend_bucket")}
                  </div>
                  <select
                    value={bucket}
                    onChange={(event) => setBucket(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                  </select>
                </div>
                <div className="flex items-end gap-3">
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      className={BTN_PRIMARY}
                      onClick={loadReport}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Run report"}
                    </button>
                    <InfoTooltip
                      text={clientSatisfactionTooltips.run_report}
                      label="Run report"
                    />
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      className={BTN_OUTLINE}
                      onClick={() => {
                        setStartDate(defaultStart);
                        setEndDate(today);
                        setBucket("week");
                      }}
                      disabled={loading}
                    >
                      Reset
                    </button>
                    <InfoTooltip
                      text={clientSatisfactionTooltips.reset_filters}
                      label="Reset"
                    />
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}

            {summary && (
              <div
                id="client-satisfaction-kpis"
                className={`grid gap-4 md:grid-cols-4 ${getSectionClassName(
                  "client-satisfaction-kpis"
                )}`}
              >
                <SummaryCard
                  label="Avg overall"
                  value={formatRating(summary.avg_overall)}
                />
                <SummaryCard
                  label="Total feedback"
                  value={summary.total_surveys ?? 0}
                />
                <SummaryCard
                  label="% 4-5"
                  value={formatPercent(summary.four_plus_percent)}
                />
                <SummaryCard
                  label="% below 4"
                  value={formatPercent(summary.below_four_percent)}
                />
              </div>
            )}

            <div
              id="client-satisfaction-details"
              className={`grid gap-4 lg:grid-cols-2 ${getSectionClassName(
                "client-satisfaction-details"
              )}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip("Criteria Averages", "criteria_averages")}
                </div>
                <div className="space-y-2">
                  {criteriaRows.map((row) => (
                    <StatRow key={row.label} label={row.label} value={row.value} />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip("Ratings Distribution", "ratings_distribution")}
                </div>
                <div className="space-y-2">
                  {distributionRows.map((row) => (
                    <div
                      key={row.rating}
                      className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                    >
                      <div className="font-semibold text-slate-700">{row.rating} stars</div>
                      <div className="text-slate-600">
                        {row.count} ({formatPercent(row.percent)})
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip("Follow-up Health", "followup_health")}
                </div>
                <div className="space-y-2">
                  <StatRow
                    label="Follow-ups created"
                    value={summary?.followups_created ?? 0}
                  />
                  <StatRow
                    label="Follow-ups open"
                    value={summary?.followups_open ?? 0}
                  />
                  <StatRow
                    label="Avg resolution"
                    value={formatHours(summary?.avg_followup_resolution_hours)}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip("Public Review Funnel", "public_review_funnel")}
                </div>
                <div className="space-y-2">
                  <StatRow
                    label="Eligible"
                    value={summary?.public_review_eligible ?? 0}
                  />
                  <StatRow
                    label="Prompted"
                    value={summary?.public_review_prompted ?? 0}
                  />
                  <StatRow
                    label="Clicked"
                    value={summary?.public_review_clicked ?? 0}
                  />
                  <StatRow
                    label="Click rate"
                    value={formatPercent(summary?.public_review_click_rate)}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip("Satisfaction trend", "satisfaction_trend")}
                </div>
                <div className="text-xs text-slate-500">
                  Average overall rating by {bucket}.
                </div>
              </div>
              {trendRows.length === 0 ? (
                <div className="text-sm text-slate-500">
                  {loading ? "Loading trend..." : "No trend data for this range."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <svg
                      viewBox="0 0 100 40"
                      className="w-full h-36 text-rose-500"
                      preserveAspectRatio="none"
                    >
                      <polyline
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        points={chartPoints.points}
                      />
                    </svg>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                    {chartPoints.rows.map((row) => (
                      <div
                        key={row.bucket_label}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1"
                      >
                        {row.bucket_label}: {formatRating(row.avg_overall)} ({row.count})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                    {renderLabelWithTooltip("Staff Breakdown", "staff_breakdown")}
                  </div>
                  <div className="text-xs text-slate-500">
                    Average scores per staff member.
                  </div>
                </div>
                <div className="inline-flex items-center gap-2">
                  <button
                    type="button"
                    className={BTN_OUTLINE}
                    onClick={() => handleExport("staff")}
                    disabled={!staffRows.length}
                  >
                    Export CSV
                  </button>
                  <InfoTooltip
                    text={clientSatisfactionTooltips.export_csv}
                    label="Export CSV"
                  />
                </div>
              </div>
              {loading && staffRows.length === 0 ? (
                <div className="text-sm text-slate-500">Loading staff...</div>
              ) : staffRows.length === 0 ? (
                <div className="text-sm text-slate-500">
                  No staff feedback data for this range.
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="w-full table-fixed text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <SortableTh
                          label="Staff"
                          sortKey="staff_name"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          className="px-4 py-2 text-left"
                        />
                        <SortableTh
                          label="Feedback"
                          sortKey="survey_count"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="Avg overall"
                          sortKey="avg_overall"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="Avg service"
                          sortKey="avg_service"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="Avg staff"
                          sortKey="avg_staff"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="% below 4"
                          sortKey="below_four_percent"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {staffSort.sortedRows.map((row) => (
                        <tr key={row.staff_user_id || row.staff_name}>
                          <td className="px-4 py-3 text-slate-900 font-semibold">
                            {row.staff_name || "Unassigned"}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {row.survey_count ?? 0}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatRating(row.avg_overall)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatRating(row.avg_service)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatRating(row.avg_staff)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatPercent(row.below_four_percent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div
              id="client-satisfaction-export"
              className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                "client-satisfaction-export"
              )}`}
            >
              Export is not available on this report.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

